import { SUBMISSION_CHECKS, type SubmissionCheck } from '@nexus/shared';

/**
 * The automatic checks, in the order they run, including the ones that never
 * ran.
 *
 * WHY SHOW WHAT DID NOT RUN. "Where did it stop" is the only question a
 * creator has when a submission is refused, and a list containing just the
 * three checks that happened cannot answer it: the greyed-out rest is what
 * says "nothing after this was even looked at", which is the difference
 * between "your clip is wrong in four ways" and "fix this one thing".
 *
 * `CONTENT_REVIEW` is a real check row but it is the operator's, not an
 * automatic one, so it is rendered after the list rather than inside it.
 */
export function CheckList({
  checks,
  stoppedAt,
}: {
  checks: SubmissionCheck[];
  /** The PLAN name the run stopped at, not the database's column. */
  stoppedAt: string | null;
}): React.JSX.Element {
  /* KEYED ON THE PLAN NAME. Two rules share the `ACCOUNT_MATCH` column —
     ownership and the campaign's follower floor — because that column's CHECK
     constraint cannot be widened on D1, and a map keyed on the column would
     collapse the two into one row wearing whichever note was written last. */
  const byPlan = new Map(checks.map((check) => [check.planName, check]));
  const review = checks.find((check) => check.checkType === 'CONTENT_REVIEW');

  /* Where the run stopped, as a position in the fixed order. Everything after
     it never ran; anything missing BEFORE it was not asked for by this
     campaign (the caption checks only exist when there are caption rules), and
     those two are different sentences. */
  const stopIndex = stoppedAt
    ? SUBMISSION_CHECKS.findIndex((meta) => meta.planName === stoppedAt)
    : -1;

  /*
   * Where the run got to, from the rows themselves.
   *
   * `stoppedAt` is the API's answer and is preferred, but it is nullable and
   * it is an enum that a later phase could rename — and when it does not
   * resolve, every missing check fell through to "Not required by this
   * campaign", which on a refused submission is a confident lie about four
   * rows in a row. The checks are written in order, so the furthest one with a
   * row is the furthest the run got, and nothing after it ran.
   */
  const lastRecorded = SUBMISSION_CHECKS.reduce(
    (furthest, meta, index) => (byPlan.has(meta.planName) ? index : furthest),
    -1,
  );
  const boundary = stopIndex >= 0 ? stopIndex : lastRecorded;

  return (
    <>
      <ol className="checklist">
        {SUBMISSION_CHECKS.map((meta, index) => {
          const ran = byPlan.get(meta.planName);
          const state = ran
            ? ran.result === 'PASS'
              ? 'pass'
              : ran.result === 'FAIL'
                ? 'fail'
                : 'exception'
            : index > boundary
              ? 'not-run'
              : 'not-required';

          return (
            <li key={meta.planName} data-check={meta.planName} data-state={state}>
              <span className="mark" aria-hidden="true">
                {state === 'pass' ? '✓' : state === 'fail' ? '✕' : state === 'exception' ? '?' : '·'}
              </span>
              <span>
                <span className="check-label">{meta.label}</span>
                {ran?.note && <div className="check-note">{ran.note}</div>}
                {!ran && (
                  <div className="check-note">
                    {state === 'not-run'
                      ? 'Not reached — the run stopped above this.'
                      : 'Not required by this campaign.'}
                  </div>
                )}
              </span>
            </li>
          );
        })}
      </ol>

      {review && (
        <div className="alert" data-tone={review.result === 'PASS' ? 'positive' : 'critical'}>
          <strong>Content review</strong>
          <span>{review.note ?? (review.result === 'PASS' ? 'Approved.' : 'Not approved.')}</span>
        </div>
      )}
    </>
  );
}
